import Vue from 'vue';
import VueRouter from 'vue-router';
import Dashboard from '@pages/Dashboard.vue';
import MockupsRoutes from '@/mockups/router/mockups-routes';
import {
  mainContactsRoutes,
  configurationContactsRoutes,
  profileContactRoutes,
  publicContactRoutes,
} from '@contacts/router/contacts-routes';
import {
  mainBookingsRoutes,
  configurationBookingsRoutes,
  publicBookingsRoutes,
} from '@bookings/router/bookings-routes';
import { TenantAuthRoutes } from '@auth/router/auth.routes';
import DocumentsRoutes from '@tools/router/documents-routes';
import ContractsRoutes from '@contracts/router/contracts-routes';
import ToolsRoutes from '@tools/router/tools-routes';
import { mainToolRoutes } from '@tools/router/tools-routes';
import CentersRoutes, {
  publicDisciplinesRoutes,
  publicResourcesRoutes,
} from '@centers/router/centers-routes';
import CoreRoutes from '@core/router/core-routes';
import SectorsRoutes from '@sectors/router/sectors-routes';
import AuthorizationService from '@auth/services/authorization.service';
import CommunicationsRoutes, {
  communicationsAdminRoutes,
} from '@communications/router/communications-routes';
import {
  configurationAccountingRoutes,
  mainAccountingRoutes,
  publicAccountingRoutes,
} from '@accounting/router/accounting-routes';
import { walletAdminRoutes } from '@wallet/router/wallet-routes';
import ActivityRoutes, {
  publicActivitiesRoutes,
  publicSummonsRoutes,
} from '@activities/router/activities-routes';
import CertificateRoutes from '@certificates/router/certificates-routes';
import { configurationPosRoutes, mainPosRoutes } from '@pos/router/pos-routes';
import {
  publicSubscriptionRoutes,
  mainSubscriptionRoutes,
} from '@subscriptions/router/subscription-routes';
import {
  postsRoutes,
  postmetaRoutes,
  widgetsRoutes,
  publicPostsRoutes,
} from '@cms/router/cms-routes';
import ModulesService from '@core/services/modules.service';
import store from '@app/store';

import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';

import BPCMessageService from '@core/services/bpc-messages.service';
import { bpcApi } from '@core/services/bpc-api.service';
import { MainAccountingIntegrationsRoutes } from '@accounting-integrations/router/accounting-integrations-routes';
import { configurationPaymentTerminalsRoutes } from '@payment-terminals/router/payment-terminals-routes';
import { entriesRoutes, publicEntriesRoutes } from '@entries/router/entries-routes';
import { subventionsRoutes } from '@subventions/router/subventions-routes';
import { $clientRouter } from '@core/services/client-router.service';
import { appConfig } from '@/config/app-config';
import {
  mainAccessControlRoutes,
  configAccessControlRoutes,
} from '@access-control/router/access-control-routes';
import ModulesHelper from '@core/services/modules.helper';
import { $translationsService } from '@core/services/translations.service';
import { MainTaxCertificateRoutes } from '@modules/tax-certificates/router/tax-certificate-routes';

const initializeTenant = (to, next) => {
  const alias = $clientRouter.getTenantAliasFromUrl();

  store.commit('tenant/clearSelectedTenant');
  store.commit('tenant/clearTenants');
  store.commit('centerSettings/clearCenterSettings');

  if (!alias || alias === appConfig.globalTenantAlias) {
    if (!to.meta.globalTenantRoute) {
      store.commit('tenant/clearTenantAlias');
      next({ name: 'auth-tenant-login' });
    } else {
      next();
    }
  } else {
    store
      .dispatch('tenant/initializeTenantByAlias', alias)
      .then(async () => {
        // Check the language to load according to the profile if the user is logged or not
        const tenantDefaultLanguage = store.getters['centerSettings/defaultLanguage'];
        const tenantAvailableLanguages = store.getters['centerSettings/availableLanguages'];
        const previousProfileLanguage = store.getters['auth/userLang'];
        const profileLanguage = store.getters['auth/profile']({
          tenantId: store.getters['tenant/selectedTenantId'],
        })?.language;

        if (profileLanguage && tenantAvailableLanguages.includes(profileLanguage)) {
          // Set the profile language in vuex to be sure we are using all the time the profile language
          store.commit('auth/setUserLang', profileLanguage);
        } else if (
          !previousProfileLanguage ||
          !tenantAvailableLanguages.includes(previousProfileLanguage)
        ) {
          // Set in vuex the default tenant language when the profile does not exist or the language is not available for the tenant
          store.commit('auth/setUserLang', tenantDefaultLanguage);
        }
        // Load translations
        await $translationsService.getTranslations(store.getters['auth/userLang']);
        next();
      })
      .catch(error => {
        const $bpcMessageService = new BPCMessageService();
        $bpcMessageService.showErrorMessage({
          text: 'global.generic.tenant_does_not_exist',
        });
        next({ name: '404-error' });
      });
  }
};

const authenticateWithQueryToken = (to, from, next) => {
  const refreshToken = to.query?.refreshToken;
  const $authorizationService = new AuthorizationService();
  if (!refreshToken) {
    next($authorizationService.getRedirectToHomePage());
  }
  const tenantAlias = $clientRouter.getTenantAliasFromUrl();

  if (tenantAlias === appConfig.globalTenantAlias) {
    next($authorizationService.getRedirectToHomePage());
  }
  store
    .dispatch('auth/refreshLogin', {
      refresh_token: refreshToken,
      tenantAlias: tenantAlias,
    })
    .then(res => {
      const redirectUrl = to.query?.redirect;
      const redirectTo = redirectUrl || $authorizationService.getRedirectToHomePage();
      next(redirectTo);
    })
    .catch(() => {
      next({ name: 'auth-tenant-login', params: { tenantAlias } });
    });
};

Vue.use(VueRouter);

const tenantAlias = $clientRouter.getTenantAliasFromUrl();
if (tenantAlias && tenantAlias !== appConfig.globalTenantAlias) {
  $clientRouter.setTenantAlias(tenantAlias);
}

// TODO Check if it is needed
// const globalAuthRoutes = !withCustomDomain ? AuthRoutes : [];

const routes = [
  {
    path: '*',
    redirect: () => {
      // TODO check this, i suspect that it is causing the
      // redirection to the login page in every refresh
      const $authorizationService = new AuthorizationService();
      return $authorizationService.getRedirectToHomePage();
    },
  },
  {
    path: '',
    name: 'home',
    meta: { isRouterView: true },
    component: () => import(/* webpackChunkName: "home" */ '@pages/RouterView.vue'),
    beforeEnter: (to, from, next) => {
      // If we want to do some checks about tenant alias in the future
      if (to.name === '404-error') {
        next();
      } else {
        initializeTenant(to, next);
      }
    },
    children: [
      {
        // NOTE This is a special page to navigate between tenants by
        // passing a refresh token by query
        path: 'qauth',
        beforeEnter: (to, from, next) => {
          authenticateWithQueryToken(to, from, next);
        },
      },
      {
        path: '404-error',
        name: '404-error',
        meta: { layout: 'bpc-userpages' },
        component: () =>
          import(/* webpackChunkName: "404-error" */ '@shared/pages/not-found-error-404.vue'),
      },
      {
        path: 'mobile-app/site/:tenantAlias/payments/result',
        name: 'mobile-app-payment-result',
        meta: { layout: 'bpc-userpages', globalTenantRoute: true },
        component: () =>
          import(
            /* webpackChunkName: "mobile-payment-result" */ '@accounting/pages/mobile-payment-result.vue'
          ),
      },
      {
        path: 'mobile-app/site/:tenantAlias/profile/bookings/:bookingEventId',
        name: 'mobile-app-booking-details',
        meta: { layout: 'bpc-userpages', globalTenantRoute: true },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "mobile-booking-details" */ '@bookings/pages/mobile-booking-details.vue'
          ),
      },
      {
        path: 'admin',
        name: 'admin',
        meta: { layout: 'bpc-admin' },
        component: () => import(/* webpackChunkName: "admin" */ '@pages/Admin.vue'),
        beforeEnter: (to, from, next) => {
          const $authorizationService = new AuthorizationService();
          if (to.meta?.module) {
            const $modulesService = new ModulesService();
            const hasModuleActivated = $modulesService.hasModuleActivated(to.meta.module);
            if (!hasModuleActivated) {
              next($authorizationService.getRedirectToHomePage(to));
              return false;
            }
          }
          if (to.meta?.modules) {
            const $modulesService = new ModulesService();
            const hasModuleActivated = $modulesService.hasSomeModuleActivated(to.meta.modules);
            if (!hasModuleActivated) {
              next($authorizationService.getRedirectToHomePage(to));
              return false;
            }
          }
          if (to.meta?.permissions) {
            const $authorizationService = new AuthorizationService();
            const hasPermission = $authorizationService.hasPermissions(to.meta.permissions);
            if (!hasPermission) {
              next($authorizationService.getRedirectToHomePage(to));
              return false;
            }
          }
          next();
        },
        children: [
          // {
          //   path: 'form-provider',
          //   name: 'bpc-form-provider-mockup',
          //   meta: { layout: 'bpc-admin' },
          //   component: () =>
          //     import(
          //       /* webpackChunkName: "bpc-form-provider-mockup" */ '@/mockups/providers/BpcFormProviderMockup.vue'
          //     ),
          // },
          {
            path: 'coming-soon',
            name: 'admin-coming-soon',
            meta: { layout: 'bpc-admin' },
            component: () => import(/* webpackChunkName: "admin" */ '@pages/ComingSoon.vue'),
          },
          {
            path: 'dashboard',
            name: 'admin-dashboard',
            meta: { layout: 'bpc-admin' },
            component: Dashboard,
          },
          {
            path: 'statistics',
            name: 'admin-statistics',
            meta: { layout: 'bpc-admin' },
            component: () =>
              import(
                /* webpackChunkName: "admin-statistics" */ '@themes/architectui/src/DemoPages/Charts/ApexCharts.vue'
              ),
          },
          {
            path: 'main',
            name: 'main',
            component: () => import(/* webpackChunkName: "main" */ '@pages/Main.vue'),
            children: [
              ...mainContactsRoutes,
              ...mainBookingsRoutes,
              ...mainToolRoutes,
              ...mainAccountingRoutes,
              ...mainSubscriptionRoutes,
              ...ActivityRoutes,
              ...CertificateRoutes,
              ...mainPosRoutes,
              ...ContractsRoutes,
              ...DocumentsRoutes,
              ...MainAccountingIntegrationsRoutes,
              ...entriesRoutes,
              ...communicationsAdminRoutes,
              ...mainAccessControlRoutes,
              ...MainTaxCertificateRoutes,
              ...walletAdminRoutes,
            ],
          },
          {
            path: 'communication',
            name: 'communication',
            component: () => import(/* webpackChunkName: "main" */ '@pages/Communication.vue'),
            children: [...CommunicationsRoutes, ...postsRoutes, ...widgetsRoutes],
          },
          {
            path: 'configuration',
            name: 'configuration',
            component: () => import(/* webpackChunkName: "main" */ '@pages/Configuration.vue'),
            children: [
              ...configurationContactsRoutes,
              ...CentersRoutes,
              ...configurationBookingsRoutes,
              ...ToolsRoutes,
              ...CoreRoutes,
              ...SectorsRoutes,
              ...configurationAccountingRoutes,
              ...postmetaRoutes,
              ...configurationPaymentTerminalsRoutes,
              ...configurationPosRoutes,
              ...subventionsRoutes,
              ...configAccessControlRoutes,
            ],
          },
        ],
      },
      ...profileContactRoutes,
      {
        path: 'public',
        name: 'public',
        meta: { layout: 'bpc-public', withSidebar: true, publicRoute: true },
        component: () => import(/* webpackChunkName: "profile" */ '@pages/public/public.vue'),
        children: [
          ...publicBookingsRoutes,
          ...publicActivitiesRoutes,
          ...publicAccountingRoutes,
          ...publicSubscriptionRoutes,
        ],
      },
      {
        path: '',
        name: 'public-website',
        meta: { layout: 'bpc-public', withSidebar: false, publicRoute: true },
        props: true,
        component: () =>
          import(/* webpackChunkName: "public-website" */ '@pages/public/public.vue'),
        children: [
          {
            path: '',
            name: 'public-homepage',
            beforeEnter: (to, from, next) => {
              const $modulesService = new ModulesService();
              const cmsActivated = $modulesService.hasModuleActivated(
                ModulesHelper.CMS_MODULE_NAME,
              );
              const cmsV2Activated = store.getters['centerSettings/centerSettings']?.use_new_cms;
              if (cmsActivated && cmsV2Activated) {
                $clientRouter.redirectToCmsV2();
              }

              next();
            },
            meta: {
              layout: 'bpc-public',
              withSidebar: false,
              publicRoute: true,
              homepage: true,
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "homepage" */ '@cms/components/details/post-details/post-details.vue'
              ),
          },
          {
            path: 'posts/:alias',
            name: 'public-post-details',
            meta: {
              layout: 'bpc-public',
              withSidebar: false,
              publicRoute: true,
              entity: 'post',
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "post-details" */ '@cms/components/details/post-details/post-details.vue'
              ),
          },
          {
            path: 'payment-invoice/:token',
            name: 'public-payment-invoice',
            meta: {
              layout: 'bpc-payment',
              withSidebar: false,
              publicRoute: true,
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "payment-invoice" */ '@accounting/pages/invoice-shopping-cart-page.vue'
              ),
          },
          {
            path: 'payment-invoice-success/:token',
            name: 'public-success-payment-invoice',
            meta: {
              layout: 'bpc-payment',
              withSidebar: false,
              publicRoute: true,
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "payment-invoice-success" */ '@accounting/pages/invoice-payment-confirmation-page.vue'
              ),
          },
          {
            path: 'payment-invoice-cancel/:token',
            name: 'public-cancel-payment-invoice',
            meta: {
              layout: 'bpc-payment',
              withSidebar: false,
              publicRoute: true,
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "payment-invoice-cancel" */ '@accounting/pages/invoice-payment-cancel-page.vue'
              ),
          },
          // {
          //   path: 'invoice-shopping-cart/:token',
          //   name: 'public-invoice-shopping-cart',
          //   meta: {
          //     layout: 'bpc-payment',
          //     withSidebar: false,
          //     publicRoute: true,
          //   },
          //   props: true,
          //   component: () =>
          //     import(
          //       /* webpackChunkName: "payment-invoice" */ '@accounting/pages/invoice-shopping-cart-page.vue'
          //     ),
          // },
          ...publicDisciplinesRoutes,
          ...publicResourcesRoutes,
          ...publicPostsRoutes,
          ...publicActivitiesRoutes,
          ...publicEntriesRoutes,
        ],
      },
      ...TenantAuthRoutes,
      ...publicContactRoutes,
      ...publicSummonsRoutes,

      // {
      //   path: '/theme',
      //   name: 'theme',
      //   // meta: { layout: 'theme' },
      //   // route level code-splitting
      //   // this generates a separate chunk (about.[hash].js) for this route
      //   // which is lazy-loaded when the route is visited.
      //   // component: () => import(/* webpackChunkName: "theme" */ '@pages/About.vue'),
      //   // component: () => import(/* webpackChunkName: "theme" */
      //   //  '@themes/architectui/src/DemoPages/Dashboards/Analytics.vue'),
      //   component: () => import(/* webpackChunkName: "admin" */ '@pages/Theme.vue'),
      //   children: [...DemoRoutes],
      // },
    ],
  },
  ...MockupsRoutes,
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0, behavior: 'smooth' };
  },
  routes,
});

export default router;
